import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { IUserModel } from '../logic/user/models/user.mode';
import { AuthService } from './auth.service';
import { HttpService } from './http-service.service';

export type SupportedLanguage = 'es' | 'en';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['es', 'en'];
export const LANGUAGE_STORAGE_KEY = 'preferredLanguage';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly currentLanguageSubject = new BehaviorSubject<SupportedLanguage>('es');
  private loadedLanguage: SupportedLanguage | null = null;

  readonly currentLanguage$: Observable<SupportedLanguage> =
    this.currentLanguageSubject.asObservable();

  constructor(
    private readonly translate: TranslateService,
    private readonly http: HttpService,
    private readonly auth: AuthService,
  ) {
    this.translate.addLangs([...SUPPORTED_LANGUAGES]);
    this.translate.setFallbackLang('es');
  }

  get currentLanguage(): SupportedLanguage {
    return this.currentLanguageSubject.value;
  }

  resolveInitialLanguage(user?: Pick<IUserModel, 'preferredLanguage'> | null): SupportedLanguage {
    const profileLang = this.normalizeLanguage(user?.preferredLanguage);
    if (profileLang) {
      return profileLang;
    }

    const storedLang = this.normalizeLanguage(
      localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? undefined,
    );
    if (storedLang) {
      return storedLang;
    }

    const browserLang = this.normalizeBrowserLang(
      navigator.language || this.translate.getBrowserLang() || '',
    );
    if (browserLang) {
      return browserLang;
    }

    return 'es';
  }

  async initialize(user?: Pick<IUserModel, 'preferredLanguage'> | null): Promise<SupportedLanguage> {
    const lang = this.resolveInitialLanguage(user);
    await this.applyLanguage(lang, { persist: false, syncAccount: false });
    return lang;
  }

  normalizeBrowserLang(raw: string): SupportedLanguage | null {
    const normalized = raw.trim().toLowerCase();
    if (!normalized) {
      return null;
    }
    if (normalized.startsWith('en')) {
      return 'en';
    }
    if (normalized.startsWith('es')) {
      return 'es';
    }
    return null;
  }

  normalizeLanguage(value?: string | null): SupportedLanguage | null {
    if (!value) {
      return null;
    }
    const normalized = value.trim().toLowerCase();
    return SUPPORTED_LANGUAGES.includes(normalized as SupportedLanguage)
      ? (normalized as SupportedLanguage)
      : null;
  }

  async setLanguage(
    lang: string,
    options: { persist?: boolean; syncAccount?: boolean } = {},
  ): Promise<void> {
    const normalized = this.normalizeLanguage(lang) ?? 'es';
    await this.applyLanguage(normalized, {
      persist: options.persist ?? true,
      syncAccount: options.syncAccount ?? true,
    });
  }

  getLocaleTag(): 'es-MX' | 'en-US' {
    return this.currentLanguage === 'en' ? 'en-US' : 'es-MX';
  }

  private async applyLanguage(
    lang: SupportedLanguage,
    options: { persist: boolean; syncAccount: boolean },
  ): Promise<void> {
    const translationsReady = this.loadedLanguage === lang;

    if (
      translationsReady &&
      this.currentLanguageSubject.value === lang &&
      this.translate.currentLang === lang
    ) {
      this.updateDocumentLang(lang);
      if (options.persist) {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      }
      return;
    }

    await firstValueFrom(this.translate.use(lang));
    this.loadedLanguage = lang;
    this.currentLanguageSubject.next(lang);
    this.updateDocumentLang(lang);

    const probe = this.translate.instant('HOME_BRANCHES.TITLE');
    const profileProbe = this.translate.instant('PROFILE_PAGE.TITLE');
    if (
      probe === 'HOME_BRANCHES.TITLE' ||
      profileProbe === 'PROFILE_PAGE.TITLE'
    ) {
      throw new Error(`Translation file for "${lang}" did not load correctly.`);
    }

    if (options.persist) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }

    if (options.syncAccount && this.auth.isAuthenticated()) {
      try {
        await firstValueFrom(this.http.updateUserLanguage(lang));
      } catch {
        // La preferencia local sigue aplicada aunque falle el sync remoto
      }
    }
  }

  private updateDocumentLang(lang: SupportedLanguage): void {
    document.documentElement.lang = lang === 'en' ? 'en-US' : 'es-MX';
  }
}
